// Dashboard MTD/AR — KPIs principales + aging buckets + top 5 vencidas.

import { useInvoicingDashboard } from '../hooks/useInvoicing';

export function InvoicingDashboard() {
  const { data, loading, error, refetch } = useInvoicingDashboard();

  if (loading && !data) return <div className="p-6 text-sm">Cargando KPIs…</div>;
  if (error)
    return (
      <div className="p-6 text-sm text-destructive">
        Error: {error} · <button onClick={refetch} className="underline">reintentar</button>
      </div>
    );
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Por cobrar (outstanding)" value={data.total_outstanding} currency={data.currency} accent="blue" />
        <Kpi label="Vencido (overdue)" value={data.total_overdue} currency={data.currency} accent="red" />
        <Kpi label="Cobrado MTD" value={data.mtd_collected} currency={data.currency} accent="green" />
      </div>

      <section>
        <h3 className="text-sm font-semibold mb-2">Aging buckets</h3>
        <div className="grid grid-cols-4 gap-3 text-sm">
          <Bucket label="0–30 días" value={data.aging_buckets['0-30']} currency={data.currency} />
          <Bucket label="31–60 días" value={data.aging_buckets['31-60']} currency={data.currency} />
          <Bucket label="61–90 días" value={data.aging_buckets['61-90']} currency={data.currency} />
          <Bucket label="90+ días" value={data.aging_buckets['90+']} currency={data.currency} highlight />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold mb-2">Top 5 facturas vencidas</h3>
        {data.top_5_overdue_invoices.length === 0 ? (
          <div className="text-sm text-muted-foreground">Sin facturas vencidas — bien hecho.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="px-2 py-1">Número</th>
                <th className="px-2 py-1">Cliente</th>
                <th className="px-2 py-1">Vence</th>
                <th className="px-2 py-1 text-right">Días</th>
                <th className="px-2 py-1 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.top_5_overdue_invoices.map((inv) => (
                <tr key={inv.id} className="border-b">
                  <td className="px-2 py-1 font-mono text-xs">{inv.number || '—'}</td>
                  <td className="px-2 py-1">{inv.customer_name}</td>
                  <td className="px-2 py-1 text-xs">{inv.due_date}</td>
                  <td className="px-2 py-1 text-right">{inv.days_overdue}</td>
                  <td className="px-2 py-1 text-right">
                    {inv.balance_due} {inv.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="text-xs text-muted-foreground">
        Actualizado: {data.as_of} · <button onClick={refetch} className="underline">refrescar</button>
      </div>
    </div>
  );
}

function Kpi({
  label, value, currency, accent,
}: { label: string; value: string; currency: string; accent: 'blue' | 'red' | 'green' }) {
  const color =
    accent === 'red' ? 'text-destructive' :
    accent === 'green' ? 'text-emerald-600' : 'text-primary';
  return (
    <div className="border rounded p-4">
      <div className="text-xs text-muted-foreground uppercase">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>
        {value} <span className="text-sm font-normal text-muted-foreground">{currency}</span>
      </div>
    </div>
  );
}

function Bucket({
  label, value, currency, highlight,
}: { label: string; value: string; currency: string; highlight?: boolean }) {
  return (
    <div className={`border rounded p-3 ${highlight ? 'border-destructive/40 bg-destructive/10' : ''}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">
        {value} <span className="text-xs text-muted-foreground">{currency}</span>
      </div>
    </div>
  );
}
